import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Composant pour gérer les transitions premium entre les pages
 */
const PageTransition = ({ children }) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState('fadeIn');

  useEffect(() => {
    if (location !== displayLocation) {
      setTransitionStage('fadeOut');
    }
  }, [location, displayLocation]);

  return (
    <div
      className={`page-transition ${transitionStage}`}
      onAnimationEnd={() => {
        if (transitionStage === 'fadeOut') {
          setTransitionStage('fadeIn');
          setDisplayLocation(location);
        }
      }}
      style={{
        animation: transitionStage === 'fadeIn' 
          ? 'fadeIn 0.5s ease-out' 
          : 'fadeOut 0.3s ease-in'
      }}
    >
      {children}
    </div>
  );
};

/**
 * Hook pour ajouter l'effet ripple sur les boutons
 */
export const useRippleEffect = () => {
  useEffect(() => {
    const addRippleEffect = (e) => {
      const button = e.currentTarget;
      
      // Éviter les doublons
      if (button.classList.contains('no-ripple')) return;
      
      const ripple = document.createElement('span');
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;

      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      ripple.classList.add('ripple');

      const rippleContainer = button.querySelector('.ripple-container');
      if (rippleContainer) {
        rippleContainer.appendChild(ripple);
      }

      setTimeout(() => {
        ripple.remove();
      }, 600);
    };

    // Ajouter l'effet à tous les boutons et liens
    const elements = document.querySelectorAll('button, a[href], .btn');
    elements.forEach((el) => {
      if (!el.querySelector('.ripple-container')) {
        const container = document.createElement('div');
        container.classList.add('ripple-container');
        el.appendChild(container);
        el.classList.add('ripple-effect');
      }
      el.addEventListener('click', addRippleEffect);
    });

    return () => {
      elements.forEach((el) => {
        el.removeEventListener('click', addRippleEffect);
      });
    };
  }, []);
};

/**
 * Composant Button Premium avec effets
 */
export const PremiumButton = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  className = '',
  ...props 
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white',
    secondary: 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white',
    success: 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white',
    premium: 'premium-button text-white',
  };

  return (
    <button
      onClick={onClick}
      className={`
        ${variants[variant]}
        ${className}
        px-6 py-3 rounded-lg font-semibold
        transition-all duration-300
        transform hover:scale-105 hover:shadow-xl
        active:scale-95
        focus:outline-none focus:ring-4 focus:ring-blue-300
        ripple-effect
        relative overflow-hidden
      `}
      {...props}
    >
      <span className="relative z-10">{children}</span>
      <div className="ripple-container absolute inset-0 overflow-hidden"></div>
    </button>
  );
};

/**
 * Composant Card Premium avec effets 3D
 */
export const PremiumCard = ({ 
  children, 
  className = '', 
  hover3D = false,
  ...props 
}) => {
  const [transform, setTransform] = useState('');

  const handleMouseMove = (e) => {
    if (!hover3D) return;
    
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;
    
    setTransform(`perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`);
  };

  const handleMouseLeave = () => {
    setTransform('');
  };

  return (
    <div
      className={`
        ${className}
        bg-white rounded-xl shadow-lg
        transition-all duration-300
        hover:shadow-2xl
        ${hover3D ? 'card-3d' : ''}
      `}
      style={{ transform }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Loader Premium
 */
export const PremiumLoader = ({ size = 'md', text = '' }) => {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className={`loader-premium ${sizes[size]}`}></div>
      {text && (
        <p className="mt-4 text-gray-600 animate-pulse">{text}</p>
      )}
    </div>
  );
};

/**
 * Badge Premium Animé
 */
export const PremiumBadge = ({ children, className = '' }) => {
  return (
    <span className={`
      badge-premium
      inline-flex items-center px-3 py-1
      bg-gradient-to-r from-blue-500 to-purple-600
      text-white text-sm font-semibold rounded-full
      ${className}
    `}>
      {children}
    </span>
  );
};

export default PageTransition;
